import { ClareServicePlan } from "@app/project/model/clare-service-plan";
import { ClareServiceSubscription } from "@app/project/model/clare-service-subscription";
import { Project } from "@app/project/model/project";
import { ServicePlanUtils } from "@app/project/services/service-plan-utils";
import { ApiResultState, LoadingState } from "@app/root-store/api-result.state";
import { ClareDbCollection } from "@app/shared/resources/resource-objectifier";
import { MemoizedSelector, createFeatureSelector, createSelector } from "@ngrx/store";
import { ServicePlanState, SubscriptionState, initialServicePlanState } from "../projects.state";

const servicePlanState = createFeatureSelector<ServicePlanState>("servicePlan");
const subscriptionState = createFeatureSelector<SubscriptionState>("subscription");

export const getServicePlanState = createSelector(servicePlanState, (state = initialServicePlanState) => state);

export const getServicePlansObj = createSelector(servicePlanState, (state = initialServicePlanState) => state.result);

export const getServicePlans = createSelector(servicePlanState, (state = initialServicePlanState): ClareServicePlan[] =>
    state.result ? Object.values(state.result) : []
);

export const servicePlansLoadingState = createSelector(servicePlanState, (state = initialServicePlanState) => state.callState);

export const subscriptionsByProjectId = createSelector(subscriptionState, (state): ClareDbCollection<ClareServiceSubscription[]> => {
    const subscriptionByProject: ClareDbCollection<ClareServiceSubscription[]> = {};
    if (state && state.subscriptions.result) {
        for (const sub in state.subscriptions.result) {
            if (state.subscriptions.result[sub]) {
                const subscription = state.subscriptions.result[sub];
                if (!subscriptionByProject[subscription.projectId]) {
                    subscriptionByProject[subscription.projectId] = [];
                }

                subscriptionByProject[subscription.projectId].push(subscription);
            }
        }
        subscriptionByProject["callState"] = state.subscriptions.callState;
    }

    return subscriptionByProject;
});

export const activeSubscription = createSelector(
    subscriptionState,
    (state): ApiResultState<ClareServiceSubscription> => (state ? state.activeSubscription : { result: null, callState: LoadingState.init })
);

/**
 * @description Selects the active service plans. If the project is provided, then the grandfathered property is checked on the
 * account. If the account is grandfathered, then the Interactive plan (self-monitoring security) should be free for life.
 * @param {Project} [project] the project with the grandfathered flag
 * @returns {MemoizedSelector<ServicePlanState, ClareServicePlan[]>} the selector
 */
export const getActiveServicePlans = (grandfathered: boolean): MemoizedSelector<ServicePlanState, ClareServicePlan[]> =>
    createSelector(getServicePlans, servicePlans =>
        servicePlans.filter(servicePlan => {
            let shouldIncludePlan = servicePlan && ServicePlanUtils.isActive(servicePlan);
            if (servicePlan?.name === "Basic" && grandfathered) {
                shouldIncludePlan = false;
            }

            return shouldIncludePlan;
        })
    );
